import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { formatTL } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const { settings } = useOutletContext() || {};
  const currency = settings?.currency_symbol || "₺";
  const threshold = settings?.free_shipping_threshold ?? 750;
  const remaining = Math.max(0, threshold - subtotal);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="cart-page">
      <h1 className="font-heading text-5xl md:text-6xl tracking-tight">Sepetiniz</h1>
      <p className="text-larc-muted mt-3">Atölyemizden seçtikleriniz</p>

      {items.length === 0 ? (
        <div className="mt-16 bg-white border border-larc-border rounded-2xl p-12 text-center" data-testid="empty-cart">
          <ShoppingBag size={32} className="mx-auto text-larc-muted" />
          <div className="font-heading text-3xl mt-4">Sepetiniz boş</div>
          <p className="text-larc-muted mt-2">Henüz hiçbir ürün eklemediniz.</p>
          <Link to="/urunler" className="mt-6 inline-flex items-center gap-2 bg-larc-gold text-white rounded-full px-8 py-3.5 text-sm hover:bg-larc-gold-hover transition-colors">
            Koleksiyonu keşfet <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {items.map((it) => (
              <div key={it.product_id} className="bg-white border border-larc-border rounded-2xl p-4 md:p-5 flex gap-4 md:gap-6">
                <Link to={`/urun/${it.slug}`} className="block w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-larc-alt shrink-0">
                  {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/urun/${it.slug}`} className="font-heading text-xl hover:text-larc-gold">{it.name}</Link>
                  <div className="text-sm text-larc-muted mt-1">{formatTL(it.price, currency)}</div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="inline-flex items-center bg-larc-alt rounded-full">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="h-9 w-9 text-larc-muted" data-testid={`cart-minus-${it.product_id}`}>−</button>
                      <span className="w-9 text-center text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="h-9 w-9 text-larc-muted" data-testid={`cart-plus-${it.product_id}`}>+</button>
                    </div>
                    <button onClick={() => removeItem(it.product_id)} className="text-larc-muted hover:text-red-600 inline-flex items-center gap-1.5 text-sm" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash2 size={14} /> Kaldır
                    </button>
                  </div>
                </div>
                <div className="text-right font-medium hidden sm:block">{formatTL(it.price * it.quantity, currency)}</div>
              </div>
            ))}
            <button onClick={clear} className="text-sm text-larc-muted hover:text-red-600 mt-2" data-testid="clear-cart">Sepeti temizle</button>
          </div>

          <aside className="bg-white border border-larc-border rounded-2xl p-6 md:p-8 h-fit sticky top-28" data-testid="cart-summary">
            <h3 className="font-heading text-2xl">Sipariş Özeti</h3>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-larc-muted">Ara toplam</span>
                <span data-testid="cart-subtotal">{formatTL(subtotal, currency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-larc-muted">Kargo</span>
                <span>{remaining > 0 ? "Hesaplanır" : "Ücretsiz"}</span>
              </div>
              {remaining > 0 && (
                <div className="bg-larc-alt rounded-xl px-4 py-3 text-xs text-larc-muted leading-relaxed">
                  Ücretsiz kargo için {formatTL(remaining, currency)} kaldı.
                </div>
              )}
            </div>
            <div className="mt-6 border-t border-larc-border pt-5 flex items-center justify-between">
              <span className="font-medium">Toplam</span>
              <span className="font-heading text-2xl">{formatTL(subtotal, currency)}</span>
            </div>
            <Link to="/odeme" className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3.5 text-sm hover:bg-larc-gold transition-colors active:scale-95" data-testid="checkout-btn">
              Siparişi Tamamla <ArrowRight size={16} />
            </Link>
          </aside>
        </div>
      )}
    </div>
  );
}
